import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import {
  getMemoizedFunction,
  getMemoizedLangFunction,
  getThumbUrl,
} from "Utils";
import AssessmentIcon from "UIComponents/AssessmentIcon";
// eslint-disable-next-line disable-autofix/import/no-namespace
import * as ENTITY_FLOW_UTILS from "./EntityFlow/utils";
// eslint-disable-next-line disable-autofix/import/no-namespace
import * as LEARNING_COURSE_FLOW_UTILS from "./LearningCourseFlow/utils";
import { getIsLearningCoursesEnabledForUnit } from "UnitPlans/modules/UnitViewModeUtils";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getTaskTypeAvatarName } from "Utils/EntityFlowUtils";
import {
  ENABLE_AI_TUTOR,
  ENABLE_ASSESSMENTS_IN_PYP,
  ENABLE_GRADE_WISE_ASSESSMENT,
} from "Constants/permissionConstants";
import {
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_WORKSHEET,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CREATE_AI_TUTOR,
  ASSESSMENT,
  ASSIGNMENT_TYPE_LE,
  ASSESSMENT_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import { Avatar } from "@toddle-edu/ds-web";
import { getIcon } from "UIComponents/NestedAccordion/utils";
import {
  ImportOutlined,
  UnitPlanOutlined,
  LeLibraryOutlined,
  JournalOutlined,
  LeOutlined,
  FaOutlined,
  SaOutlined,
  AssessmentAddOutlined,
  WorksheetClipboardOutlined,
  LinkOutlined,
  NoteOutlined,
  UploadOutlined,
  GoogleDriveColored,
  OneDriveColored,
  DeviceDesktopOutlined,
  AiOutlined,
  ResourceOutlined,
  AssessmentOutlined,
  DotsHorizontalOutlined,
} from "@toddle-edu/ds-icons";
import { RESOURCES_KEY } from "./CommonUtils";
import { getAssessmentTemplatesFromCache } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";

const getResourceTypeOptions = ({ t }) => {
  return [
    {
      key: RESOURCES_KEY,
      value: RESOURCES_KEY,
      label: t("common:resources"),
      prefixElement: <ResourceOutlined />,
    },
  ];
};

const getFilterDropdownOptions = ({
  t,
  curriculumType,
  userType,
  isWorksheetEnabledForToddleLibrary = false,
}) => {
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);
  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets =
    ACLStore.can("FeatureFlag:ClassroomWorksheet") ||
    isWorksheetEnabledForToddleLibrary;

  const canCreateAIChatTask = ACLStore.can(ENABLE_AI_TUTOR);

  const assessmentTypeOptions = [
    {
      label: t("common:le_label"),
      key: ASSIGNMENT_TYPE_LE,
      value: ASSIGNMENT_TYPE_LE,
      prefixElement: <LeOutlined />,
    },
    ...(curriculumType === CURRICULUM_TYPE_UBD || canCreateAssessments
      ? [
          {
            label: t("common:assessment_label_plural"),
            key: ASSESSMENT,
            value: ASSESSMENT,
            prefixElement: <AssessmentOutlined />,
          },
        ]
      : []),
    ...(canCreateWorksheets
      ? [
          {
            label: t("common:worksheet"),
            key: ASSESSMENT_TYPE_WORKSHEET,
            value: ASSESSMENT_TYPE_WORKSHEET,
            prefixElement: <WorksheetClipboardOutlined />,
          },
        ]
      : []),
    ...(canCreateAIChatTask
      ? [
          {
            label: t("common:ai_tutor"),
            key: ASSESSMENT_TYPE_AI_TUTOR,
            value: ASSESSMENT_TYPE_AI_TUTOR,
            prefixElement: <AiOutlined />,
          },
        ]
      : []),
  ];

  const resourceTypeOptions = getResourceTypeOptions({ t });

  // Return only resource options for parent/student
  if (_.includes(["parent", "student"], userType)) {
    return resourceTypeOptions;
  }
  return [...assessmentTypeOptions, ...resourceTypeOptions];
};

export const getFilterDropdownOptionsMemoized = getMemoizedLangFunction(
  getFilterDropdownOptions
);

const getResourceTitleMemoized = getMemoizedLangFunction(({ t, title }) => {
  return _.isEmpty(title) ? t("common:untitled") : title;
});

export const getCardLabel = ({ libraryItem }) => {
  const { label, resourceType, item, attachment } = libraryItem;

  let text = getResourceTitleMemoized({ title: label });

  switch (resourceType) {
    case "ASSESSMENT":
      text = getResourceTitleMemoized({
        title: _.get(item, "title.value", ""),
      });
      break;

    case "ASSIGNMENT":
      text = _.get(item, "content.title.value", "--");
      break;

    case "FILE": {
      const isNote = attachment?.type === "NOTE";
      const attachmentName = _.get(attachment, "name", "");
      text = isNote ? _.get(attachment, "title", "--") : attachmentName;
      break;
    }
  }

  return text;
};

export const getIconAndThumbUrl = getMemoizedFunction(
  ({ libraryItem, classes }) => {
    const { resourceType, item, attachment } = libraryItem;
    let icon = null;
    let thumbUrl = null;
    switch (resourceType) {
      case "ASSESSMENT": {
        const itemData =
          resourceType == "ASSESSMENT" ? item : _.get(item, "content", {});
        const assessmentType = _.get(itemData, "assessmentType.value");
        const imageUrl = _.get(itemData, "image.value", null);

        if (imageUrl) {
          thumbUrl = imageUrl;
        } else {
          icon = (
            <AssessmentIcon
              assessmentType={assessmentType}
              iconSize={"medium"}
            />
          );
        }
        break;
      }

      default: {
        const { thumbUrl: _thumbUrl } = getThumbUrl({
          attachment,
        });
        if (_.get(attachment, "type") === "NOTE") {
          icon = getIcon({ orgResource: libraryItem, classes });
        } else {
          thumbUrl = _thumbUrl;
          break;
        }
      }
    }
    return { icon, thumbUrl };
  },
  ({ libraryItem }) => {
    return JSON.stringify(libraryItem);
  }
);

export const getAssessmentTypeLocale = {
  ASSESSMENT: "common:assessment_label",
  LEARNING_ENGAGEMENT: "common:le_label",
  WORKSHEET: "common:worksheet",
  AI_TUTOR: "common:ai_tutor",
};

export const getCombinedLibraryUtils = () =>
  getIsLearningCoursesEnabledForUnit()
    ? LEARNING_COURSE_FLOW_UTILS
    : ENTITY_FLOW_UTILS;

export const getAddDropDownOptions = ({
  t,
  curriculumType,
  isTermsExists,
  isUnlockedTermExistsForCurrentLearningCourse = true,
}) => {
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);

  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets = ACLStore.can("FeatureFlag:ClassroomWorksheet");
  const canCreateAIChatTask = ACLStore.can(ENABLE_AI_TUTOR);
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  return [
    {
      label: "common:add",
      key: "CREATE",
      isItemGroup: true,
      options: [
        {
          label: "classRoom:create_le",
          key: "CREATE_LE",
          icon: <LeOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("classRoom:create_le") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        ...(canCreateAssessments
          ? [
              {
                label: "classRoom:create_fa",
                key: "CREATE_FA",
                icon: <FaOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("classRoom:create_fa") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
              {
                label: "classRoom:create_sa",
                key: "CREATE_SA",
                icon: <SaOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("classRoom:create_sa") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(curriculumType === CURRICULUM_TYPE_UBD
          ? [
              {
                label: "classRoom:create_assessment_classroom",
                key: "CREATE_ASSESSMENT",
                icon: <AssessmentAddOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("classRoom:create_assessment_classroom") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(canCreateWorksheets
          ? [
              {
                label: "classMaterials:create_a_worksheet",
                key: "CREATE_WORKSHEET",
                value: ASSESSMENT_TYPE_WORKSHEET,
                icon: <WorksheetClipboardOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("classMaterials:create_a_worksheet") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        ...(canCreateAIChatTask
          ? [
              {
                label: "classRoom:create_an_ai_tutor",
                key: CREATE_AI_TUTOR,
                value: ASSESSMENT_TYPE_AI_TUTOR,
                icon: <AiOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                      { label: t("common:ai_tutor") }
                    )
                  : t(
                      "course:item_cannot_be_added_due_to_locked_grading_periods"
                    ),
              },
            ]
          : []),
        {
          label: "common:add_note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:add_link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        ...(isMapleBearLikeOrg
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:import_from_label_no_grading_periods_tooltip",
                      {
                        label: t("common:toddle_library"),
                      }
                    )
                  : t(
                      "course:items_cannot_be_imported_due_to_locked_grading_periods"
                    ),
              },
            ]),
      ],
    },
  ];
};

export const getTemplateIdFromTaskType = (
  taskTypeId,
  gradeId,
  curriculumId,
  isGradeWiseAssessment = false
) => {
  const data = getAssessmentTemplatesFromCache(curriculumId);

  if (_.isEmpty(data)) {
    return null;
  }

  const taskTypes = _.get(data, "taskTypes", []);

  const taskType = _.find(taskTypes, { id: taskTypeId });

  if (_.isEmpty(taskType)) {
    return null;
  }

  const templates = _.get(taskType, "templates", []);

  // Filter templates by grade if grade-wise assessment is enabled and gradeId is provided
  if (isGradeWiseAssessment && gradeId) {
    const filteredTemplates = _.filter(templates, template => {
      const templateGrades = _.get(template, "grades", []);
      return _.some(templateGrades, grade => grade.id === gradeId);
    });

    if (_.isEmpty(filteredTemplates)) {
      return _.get(_.first(templates), "id", null);
    } else {
      return _.get(_.first(filteredTemplates), "id", null);
    }
  }

  // When not filtering by grade, return the first template
  return _.get(_.first(templates), "id", null);
};

const getAddDropDownOptionsV2 = ({
  t,
  curriculumProgramId,
  isTermsExists,
  isUnlockedTermExistsForCurrentLearningCourse = true,
  gradeId,
}) => {
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  const assessmentTemplatesData = getAssessmentTemplatesFromCache(
    curriculumProgramId
  );
  const taskTypes = _.get(assessmentTemplatesData, "taskTypes", []);

  const sortedTaskTypes = _.sortBy(taskTypes, "displaySequence");

  const filteredTaskTypes = _.filter(
    sortedTaskTypes,
    taskType =>
      !_.includes([ASSESSMENT_TYPE_QUICK_TASK], _.get(taskType, "type", ""))
  );

  const isGradeWiseAssessment = ACLStore.can(ENABLE_GRADE_WISE_ASSESSMENT);
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const assessmentOptions = _.map(filteredTaskTypes, taskType => {
    const type = _.get(taskType, "type", "");
    const label = _.get(taskType, "label", "");
    const color = _.get(taskType, "color", "");
    const taskTypeId = _.get(taskType, "id", "");

    const templateId = getTemplateIdFromTaskType(
      taskTypeId,
      gradeId,
      curriculumProgramId,
      isGradeWiseAssessment
    );

    const avatarName = getTaskTypeAvatarName({
      type,
      label,
      isAssessmentTypeConfigurationEnabled,
    });

    return {
      label: label,
      key: taskTypeId,
      value: taskTypeId,
      taskType: type,
      templateId,
      icon: (
        <Avatar
          shape="square"
          size="large"
          name={avatarName}
          color={color}
          dsVersion="2.0"
        />
      ),
      disabled: !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
      disabledTooltip: !isTermsExists
        ? t(
            "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
            { label }
          )
        : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
    };
  });

  return [
    {
      label: "common:add",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...assessmentOptions,
        {
          label: "common:add_note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:add_link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: [
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        ...(isMapleBearLikeOrg
          ? []
          : [
              {
                key: "IMPORT_FROM_TODDLE_LIBRARY",
                label: "common:toddle_library",
                icon: <JournalOutlined />,
                disabled:
                  !isTermsExists ||
                  !isUnlockedTermExistsForCurrentLearningCourse,
                disabledTooltip: !isTermsExists
                  ? t(
                      "classRoom:import_from_label_no_grading_periods_tooltip",
                      {
                        label: t("common:toddle_library"),
                      }
                    )
                  : t(
                      "course:items_cannot_be_imported_due_to_locked_grading_periods"
                    ),
              },
            ]),
      ],
    },
  ];
};

export const getAddDropDownOptionsMemoized = getMemoizedLangFunction(
  getAddDropDownOptions
);

export const getAddDropDownOptionsV2Memoized = getMemoizedLangFunction(
  getAddDropDownOptionsV2
);

export const getHeaderText = ({ userType, isPYPAssessmentEnabled, isPYP }) => {
  switch (userType) {
    case "parent":
    case "student":
      return "common:resources";

    case "staff":
      if (!isPYPAssessmentEnabled && isPYP) {
        return "unitPlan:learning_experiences_and_resources";
      }
      return "unitPlan:assessments_and_resources";
  }
};
